import { createSelector } from "@reduxjs/toolkit";

import { ACTIVE, INACTIVE } from "../../consts/driverDetails";
import { DRIVER_SORT_TYPES, SORT_ORDERS } from "../../consts/sorters";
import { NONE } from "../../consts/strings";
import {
  CONTRACTOR_DRIVER_TYPES,
  DRIVER_TYPE_NAMES,
  DR_DRIVER_TYPE,
  EMPLOYED_DRIVER_TYPES,
  ODF_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../consts/driverTypes";

import {
  getDriversImpactedWeekCombined,
  isDriverActive,
} from "../../utils/drivers";
import { driversSorters } from "../../utils/sorters";

import { driversSorterSelector, sortOrderSelector } from "../sorters/selectors";
import { filtersSelector } from "../filters/selectors";
import { selectedDriverSelector } from "../ownApp/selectors";

export const driverManagementLoadingSelector = (state) =>
  state.driverManagement.loading;
export const driverViewSelector = (state) => state.driverManagement.view;
export const selectingDriverLocationSelector = (state) =>
  state.driverManagement.selectingDriverLocation;
export const hideDriverManagementModalSelector = (state) =>
  state.driverManagement.hideDriverManagementModal;

export const driverManagementSessionChecksumSelector = (state) =>
  state.driverManagement.sessionChecksum;
export const userSessionExpiredSelector = (state) =>
  state.driverManagement.userSessionExpired;
export const locksRefreshRequiredSelector = (state) =>
  state.driverManagement.locksRefreshRequired;
export const driverProfilesSelector = (state) =>
  state.driverManagement.driverProfiles;
export const driverProfileSelector = (driverCode) => (state) =>
  state.driverManagement.driverProfiles[driverCode];

export const vehiclesSelector = (state) => state.driverManagement.vehicles;
export const countriesSelector = (state) => state.driverManagement.countries;
export const sectionDaysSelector = (state) =>
  state.driverManagement.sectionDays;
export const sectionDaySelector = (dayOfWeek) => (state) =>
  state.driverManagement.sectionDays[dayOfWeek];

export const driverProfileLocksSelector = (state) =>
  state.driverManagement.driverProfileLocks;
export const driverProfileLockSelector = (driverCode) => (state) =>
  state.driverManagement.driverProfileLocks[driverCode];

export const matrixDaysSelector = (state) => state.driverManagement.matrixDays;
export const matrixDaySelector = (dayOfWeek) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek];

export const matrixDayDriversSelector = (dayOfWeek) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].drivers;
export const matrixDayDriverSelector = (dayOfWeek, driverCode) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].drivers[driverCode];

export const matrixDayRoutesSelector = (dayOfWeek) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].routes;
export const matrixDayRouteSelector = (dayOfWeek, routeCode) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].routes[routeCode];

export const driverDayLocksSelector = (dayOfWeek) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].driverDayLocks;
export const driverDayLockSelector = (dayOfWeek, driverCode) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].driverDayLocks[driverCode];
export const driverWeekLocksSelector = (driverCode) => (state) =>
  [0, 1, 2, 3, 4, 5, 6].reduce((acc, dayOfWeek) => {
    acc[dayOfWeek] =
      state.driverManagement.matrixDays[dayOfWeek].driverDayLocks[driverCode];
    return acc;
  }, {});

export const routeDayLocksSelector = (dayOfWeek) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].routeDayLocks;
export const routeDayLockSelector = (dayOfWeek, routeCode) => (state) =>
  state.driverManagement.matrixDays[dayOfWeek].routeDayLocks[routeCode];

export const anyDriverDayLockedSelector = createSelector(
  matrixDaysSelector,
  (matrixDays) =>
    Object.keys(matrixDays).some((dayOfWeek) =>
      Object.values(matrixDays[dayOfWeek].driverDayLocks).some(
        ({ lockedByUserCode }) => lockedByUserCode
      )
    )
);
export const anyRouteDayLockedSelector = createSelector(
  matrixDaysSelector,
  (matrixDays) =>
    Object.keys(matrixDays).some((dayOfWeek) =>
      Object.values(matrixDays[dayOfWeek].routeDayLocks).some(
        ({ lockedByUserCode }) => lockedByUserCode
      )
    )
);

export const isDriverEditProfileValidSelector = (state) =>
  state.driverManagement.isDriverEditProfileValid;
export const isDriverEditWorkScheduleValidSelector = (state) =>
  state.driverManagement.isDriverEditWorkScheduleValid;
export const driversImpactedSelector = (state) =>
  state.driverManagement.driversImpacted;
export const driversImpactedWeekCombinedSelector = createSelector(
  driversImpactedSelector,
  (driversImpacted) => getDriversImpactedWeekCombined(driversImpacted)
);
export const driverImagesSelector = (state) =>
  state.driverManagement.driverImages;
export const driverImageSelector = (driverCode) => (state) =>
  state.driverManagement.driverImages[driverCode];

export const newDriverEditSelector = (state) =>
  state.driverManagement.newDriverEdit;
export const newDriverUsersSelector = (state) =>
  state.driverManagement.newDriverEdit?.users || null;
export const newDriverSelectedUserSelector = (state) =>
  state.driverManagement.newDriverEdit?.selectedUser || {
    userId: NONE,
    userName: NONE,
  };
export const newDriverTypesSelector = (state) =>
  state.driverManagement.newDriverEdit?.driverTypes || [
    { label: NONE, value: NONE },
  ];
export const newDriverCodeSelector = (state) =>
  state.driverManagement.newDriverEdit?.driverProfileEdit?.driverCode || "";
export const newDriverProfileEditSelector = (state) =>
  state.driverManagement.newDriverEdit?.driverProfileEdit || null;

export const driverEditModeSelector = (state) =>
  !!state.driverManagement.driverEdit;
export const driverEditSelector = (state) => state.driverManagement.driverEdit;
export const driverEditCopySelector = (state) =>
  state.driverManagement.driverEditCopy;
export const driverEditCodeSelector = (state) =>
  driverEditSelector(state)?.driverCode || null;
export const driverProfileEditSelector = (state) =>
  driverEditSelector(state)?.driverProfileEdit || null;
export const driverEditTypeSelector = (state) =>
  driverProfileEditSelector(state)?.driverType || null;
export const driverDayEditSelector = (dayOfWeek) => (state) =>
  driverEditSelector(state)?.driverDayEdits?.[dayOfWeek] || null;
export const driverDayEditCopySelector = (dayOfWeek) => (state) =>
  driverEditCopySelector(state)?.driverDayEdits?.[dayOfWeek] || null;
export const driverDayEditsSelector = (state) =>
  driverEditSelector(state)?.driverDayEdits || null;

export const driverTypesSelector = createSelector(
  selectedDriverSelector,
  driverEditSelector,
  (selectedDriver, driverEdit) => {
    if (!selectedDriver && !driverEdit) return [];

    const { driverProfileEdit } = driverEdit || selectedDriver;
    const { driverType } = driverProfileEdit;

    if (driverType === ODF_DRIVER_TYPE)
      return [
        { value: ODF_DRIVER_TYPE, label: DRIVER_TYPE_NAMES[ODF_DRIVER_TYPE] },
      ];
    else if (driverType === DR_DRIVER_TYPE)
      return [
        { value: DR_DRIVER_TYPE, label: DRIVER_TYPE_NAMES[DR_DRIVER_TYPE] },
      ];
    else if (driverType === ROBOT_DRIVER_TYPE)
      return [
        {
          value: ROBOT_DRIVER_TYPE,
          label: DRIVER_TYPE_NAMES[ROBOT_DRIVER_TYPE],
        },
      ];
    else if (!!EMPLOYED_DRIVER_TYPES[driverType])
      return Object.entries(EMPLOYED_DRIVER_TYPES).map(([value, label]) => ({
        value,
        label,
      }));
    else if (!!CONTRACTOR_DRIVER_TYPES[driverType])
      return Object.entries(CONTRACTOR_DRIVER_TYPES).map(([value, label]) => ({
        value,
        label,
      }));
    else return [];
  }
);

export const driverOptionsSelector = createSelector(
  driverProfilesSelector,
  (drivers) => {
    if (!drivers) return [];
    return Object.values(drivers).map(({ driverCode, driverName }) => {
      return {
        value: `${driverCode.split("*")[1]} - ${driverName}`,
        driverCode,
      };
    });
  }
);

export const driverListWithActivitySelector = createSelector(
  driverProfilesSelector,
  matrixDaysSelector,
  (driverProfiles, matrixDays) => {
    if (!driverProfiles || !matrixDays) return [];
    else
      return Object.values(driverProfiles).map((driverProfile) => ({
        ...driverProfile,
        isActive: isDriverActive({ driverProfile, matrixDays }),
      }));
  }
);

export const sortedDriverListWithFiltersSelector = createSelector(
  driverEditSelector,
  driverListWithActivitySelector,
  vehiclesSelector,
  filtersSelector,
  sortOrderSelector,
  driversSorterSelector,
  (driverEdit, drivers, vehicles, filters, sortOrder, sorter) => {
    if (!driverEdit)
      Object.keys(filters).forEach((filterType) => {
        drivers = drivers.filter((driver) => {
          const driverVehicle = vehicles[driver.vehicleType];
          return filters[filterType].filterCallback({
            driver,
            vehicle: driverVehicle,
            ...filters[filterType].functionParameters,
          });
        });
      });

    const driverNumberSorter = driversSorters[DRIVER_SORT_TYPES.DRIVER_NUMBER];
    const sortedDrivers = [...drivers].sort((a, b) =>
      driverNumberSorter({ driver: a }, { driver: b })
    );

    if (sorter === driverNumberSorter && sortOrder === SORT_ORDERS.DESC)
      return sortedDrivers;
    else
      return sortedDrivers.sort((a, b) => sorter({ driver: a }, { driver: b }));
  }
);

export const driversListSelector = createSelector(
  driverViewSelector,
  sortedDriverListWithFiltersSelector,
  (view, drivers) => {
    switch (view) {
      case ACTIVE:
        return drivers.filter((driver) => driver.isActive);
      case INACTIVE:
        return drivers.filter((driver) => !driver.isActive);
      default:
        return drivers;
    }
  }
);

export const reorderedDriversListSelector = createSelector(
  driversListSelector,
  driversImpactedWeekCombinedSelector,
  driverEditSelector,
  (drivers, driversImpacted, driverEdit) => {
    if (!driverEdit || !Object.keys(driversImpacted).length) return drivers;

    const driversAffected = [],
      otherDrivers = [];
    drivers.forEach((driver) => {
      if (
        driversImpacted[driver.driverCode] &&
        driver.driverCode !== driverEdit.driverCode
      )
        driversAffected.push(driver);
      else otherDrivers.push(driver);
    });

    const driverEditIndex = otherDrivers.findIndex(
      (driver) => driver.driverCode === driverEdit.driverCode
    );

    return otherDrivers
      .slice(0, driverEditIndex + 1)
      .concat(driversAffected)
      .concat(otherDrivers.slice(driverEditIndex + 1, otherDrivers.length));
  }
);
